import { memo, useCallback } from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import TextAreaInput from "../inputs/TextAreaInput";
import { formatJsonData } from "../../lib/functions";
import { useExecuteViewQuery } from "../../hooks/internal-config/mutation";

export const QueryConfigCardSchema = Yup.object({
  password: Yup.string().required("Password is required"),
  query: Yup.string().required("Query is required"),
  response: Yup.string(),
});

interface FormValues {
  password: string;
  query: string;
  response?: string;
}

const QueryConfigCard = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useFormWithSchema(QueryConfigCardSchema);

  const response = watch("response");

  const {
    mutateAsync: ExecuteViewQuery,
    isPending: isExecuteViewQueryLoading,
  } = useExecuteViewQuery();

  const onSubmit = useCallback(
    async (data: FormValues) => {
      let res;
      try {
        const body = {
          query: data.query,
          password: data.password,
        };

        res = await ExecuteViewQuery(body);
      } catch (error: any) {
        res = JSON.stringify(error, null, 2);
      } finally {
        const formattedRes =
          Array.isArray(res) || typeof res === "object"
            ? JSON.stringify(res, null, 2)
            : res || "";

        setValue("response", formattedRes);
      }
    },
    [ExecuteViewQuery, setValue]
  );

  const handleFormatJSONData = useCallback(() => {
    if (!response) return;

    const formattedData = formatJsonData(response);

    setValue("response", formattedData);
  }, [response, setValue]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col  gap-3 lg:gap-6"
    >
      <h2 className="text-lg lg:text-xl font-medium">Query</h2>
      <div className="flex flex-col gap-3 lg:gap-6">
        <div className="grid md:grid-cols-2 gap-3 lg:gap-6">
          <Controller
            name="password"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label="Password"
                onChange={onChange}
                value={value || ""}
                error={errors?.[name]?.message}
                required
              />
            )}
          />
        </div>
        <Controller
          name="query"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextAreaInput
              label="Query"
              onChange={onChange}
              value={value || ""}
              error={errors?.[name]?.message}
              required
            />
          )}
        />

        <Button className="max-w-max" isLoading={isExecuteViewQueryLoading}>
          {t("Execute View Query")}
        </Button>

        <div className="relative">
          <Button
            type="button"
            disabled={!response}
            onClick={handleFormatJSONData}
            className="absolute top-0 right-0 z-30"
          >
            {t("Format JSON Data")}
          </Button>
          <div className="mt-3">
            <Controller
              name="response"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextAreaInput
                  label="Response"
                  onChange={onChange}
                  value={value || ""}
                  readOnly
                  rows={15}
                />
              )}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default memo(QueryConfigCard);
